import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Eye, EyeOff, Info, AlertTriangle, Shield,
  CheckCircle, XCircle, HelpCircle, Megaphone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * GROUP CREATION — REQUIRED POLICY GATING (LOCKED DIRECTIVE)
 * 
 * No group can be created without completing ALL items.
 * 
 * A. Group Basics: Name, Description, Type
 * B. Visibility: Public / Private
 * C. PROMOTION POLICY (CRITICAL — DEFAULT = NO)
 * D. Steward Accountability: Acknowledgment checkbox
 * E. Create Group
 */

// Group Types (LOCKED)
const GROUP_TYPES = [
  { id: 'community', label: 'Community', description: 'General community gathering' },
  { id: 'activity_events', label: 'Activity / Events', description: 'Scheduled activities or events' },
  { id: 'learning_education', label: 'Learning / Education', description: 'Educational content and courses' },
  { id: 'support', label: 'Support', description: 'Support and assistance groups' },
  { id: 'seasonal', label: 'Seasonal', description: 'Time-limited seasonal activities' }
];

// Promotion Policies (LOCKED)
const PROMOTION_POLICIES = [
  { 
    id: 'no_promotion', 
    label: 'No promotional content allowed', 
    icon: XCircle,
    iconColor: 'text-red-500',
    isDefault: true,
    description: 'Business offers, services, paid events, external sales, or self-promotion beyond the group\'s stated purpose are not permitted.'
  },
  { 
    id: 'with_approval', 
    label: 'Promotional content allowed with steward approval',
    icon: HelpCircle,
    iconColor: 'text-amber-500',
    isDefault: false,
    description: 'Stewards must review and approve promotional content before it can be posted.'
  },
  { 
    id: 'marked_allowed', 
    label: 'Promotional content allowed and clearly marked',
    icon: CheckCircle,
    iconColor: 'text-green-500',
    isDefault: false,
    description: 'Promotional content is allowed but must be clearly identified as such.'
  }
];

// Promotional Content Definition (LOCKED COPY)
const PROMOTIONAL_CONTENT_TOOLTIP = `Promotional content includes business offers, services, paid events, external sales, or self-promotion beyond the group's stated purpose.`;

export default function CreateGroup() {
  const navigate = useNavigate();
  const { user, authResolved } = useContext(AuthContext);
  
  // Form state - MANDATORY POLICY GATING
  const [formData, setFormData] = useState({
    // A. GROUP BASICS
    name: '',
    description: '',
    groupType: '',
    
    // B. VISIBILITY
    visibility: 'public',
    
    // C. PROMOTION POLICY (DEFAULT = NO)
    promotionPolicy: 'no_promotion',
    
    // D. STEWARD ACCOUNTABILITY
    stewardAcknowledgment: false
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Wait for the auth boot to resolve before redirecting; otherwise direct
    // URL navigation (e.g. /create-group) bounces unauthenticated-looking
    // users out before the cookie/session check completes.
    if (!authResolved) return;
    if (!user) {
      navigate('/auth');
      return;
    }
    // V1 decoupling: group creation is never gated by membership tier.
  }, [user, authResolved, navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    // A. GROUP BASICS
    if (!formData.name.trim()) {
      newErrors.name = 'Group name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.groupType) {
      newErrors.groupType = 'Group type is required';
    }
    
    // C. PROMOTION POLICY
    if (!formData.promotionPolicy) {
      newErrors.promotionPolicy = 'Promotion policy selection is required';
    }
    
    // D. STEWARD ACCOUNTABILITY
    if (!formData.stewardAcknowledgment) {
      newErrors.stewardAcknowledgment = 'Steward acknowledgment is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please complete all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const groupData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        group_type: formData.groupType,
        visibility: formData.visibility,
        promotion_policy: formData.promotionPolicy,
        steward_acknowledgment: formData.stewardAcknowledgment
      };
      
      const response = await axios.post(`${API}/api/groups`, groupData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Group created successfully!');
      
      // Navigate to the new group profile
      const groupId = response.data?.group_id;
      if (groupId) {
        navigate(`/group/${groupId}`);
      } else {
        navigate('/');
      }
      
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 hover:opacity-70">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>Create Group</h1>
            <div className="w-10" />
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* ═══ A. GROUP BASICS ═══ */}
          <Card className="p-5" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Users className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              A. Group Basics
            </h2>
            
            {/* Group Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                Group Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Saturday Hikers, Book Club"
                className={errors.name ? 'border-red-500' : ''}
                maxLength={100}
                data-testid="group-name-input"
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            {/* Short Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                Short Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe the purpose of this group (max 1 paragraph)"
                rows={3}
                maxLength={500}
                className={`w-full p-3 rounded-lg text-sm ${errors.description ? 'border-red-500' : ''}`}
                style={{ 
                  backgroundColor: 'var(--fy-surface-muted)', 
                  border: '1px solid var(--fy-border-subtle)',
                  color: 'var(--fy-text-primary)'
                }}
                data-testid="group-description-input"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                {formData.description.length}/500 characters
              </p>
              {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
            </div>

            {/* Group Type */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                Group Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 gap-2">
                {GROUP_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleInputChange('groupType', type.id)}
                    className="flex items-center gap-3 p-3 rounded-lg text-left transition-colors"
                    style={{
                      backgroundColor: formData.groupType === type.id ? 'var(--lk-sage-muted)' : 'var(--fy-surface-muted)',
                      border: `2px solid ${formData.groupType === type.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`
                    }}
                    data-testid={`group-type-${type.id}`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      formData.groupType === type.id ? 'border-[var(--lk-sage)] bg-[var(--lk-sage)]' : 'border-gray-400'
                    }`}>
                      {formData.groupType === type.id && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>{type.label}</p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{type.description}</p>
                    </div>
                  </button>
                ))}
              </div>
              {errors.groupType && <p className="text-xs text-red-500 mt-1">{errors.groupType}</p>}
            </div>
          </Card>

          {/* ═══ B. VISIBILITY ═══ */}
          <Card className="p-5" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Eye className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              B. Visibility
            </h2>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange('visibility', 'public')}
                className="flex items-center gap-2 p-4 rounded-xl text-left transition-colors"
                style={{
                  backgroundColor: formData.visibility === 'public' ? 'var(--lk-sage-muted)' : 'var(--fy-surface-muted)',
                  border: `2px solid ${formData.visibility === 'public' ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`
                }}
                data-testid="visibility-public"
              >
                <Eye className="w-5 h-5" style={{ color: formData.visibility === 'public' ? 'var(--lk-sage)' : 'var(--fy-text-secondary)' }} />
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>Public</p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Discoverable, join request</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleInputChange('visibility', 'private')}
                className="flex items-center gap-2 p-4 rounded-xl text-left transition-colors"
                style={{
                  backgroundColor: formData.visibility === 'private' ? 'var(--lk-sage-muted)' : 'var(--fy-surface-muted)',
                  border: `2px solid ${formData.visibility === 'private' ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`
                }}
                data-testid="visibility-private"
              >
                <EyeOff className="w-5 h-5" style={{ color: formData.visibility === 'private' ? 'var(--lk-sage)' : 'var(--fy-text-secondary)' }} />
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>Private</p>
                  <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Invite only</p>
                </div>
              </button>
            </div>
          </Card>

          {/* ═══ C. PROMOTION POLICY (CRITICAL) ═══ */}
          <Card className="p-5" style={{ backgroundColor: 'var(--fy-surface-card)', border: '2px solid var(--lk-terra)' }}>
            <h2 className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Megaphone className="w-5 h-5" style={{ color: 'var(--lk-terra)' }} />
              C. Promotion Policy <span className="text-red-500">*</span>
            </h2>
            
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(var(--lk-terra-rgb), 0.1)' }}>
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                Does this group allow promotional content?
              </p>
              <button
                type="button"
                onClick={() => setShowTooltip(!showTooltip)}
                className="text-xs underline mt-1 flex items-center gap-1"
                style={{ color: 'var(--lk-terra)' }}
              >
                <Info className="w-3 h-3" />
                What is promotional content?
              </button>
              {showTooltip && (
                <p className="text-xs mt-2 p-2 rounded" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
                  {PROMOTIONAL_CONTENT_TOOLTIP}
                </p>
              )}
            </div>

            <div className="space-y-2">
              {PROMOTION_POLICIES.map((policy) => {
                const Icon = policy.icon;
                return (
                  <button
                    key={policy.id}
                    type="button"
                    onClick={() => handleInputChange('promotionPolicy', policy.id)}
                    className="w-full flex items-start gap-3 p-4 rounded-lg text-left transition-colors"
                    style={{
                      backgroundColor: formData.promotionPolicy === policy.id ? 'var(--lk-sage-muted)' : 'var(--fy-surface-muted)',
                      border: `2px solid ${formData.promotionPolicy === policy.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`
                    }}
                    data-testid={`promotion-policy-${policy.id}`}
                  >
                    <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${policy.iconColor}`} />
                    <div>
                      <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                        {policy.label}
                        {policy.isDefault && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">DEFAULT</span>
                        )}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                        {policy.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            {errors.promotionPolicy && <p className="text-xs text-red-500 mt-2">{errors.promotionPolicy}</p>}
          </Card>

          {/* ═══ D. STEWARD ACCOUNTABILITY ═══ */}
          <Card className="p-5" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Shield className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              D. Steward Accountability
            </h2>
            
            <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                You will be the steward of this group. At least one steward is required.
              </p>
              <p className="text-xs mt-2 font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                Steward: @{user.username || 'you'}
              </p>
            </div>
            
            <label className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer ${
              errors.stewardAcknowledgment ? 'border-2 border-red-500' : 'border border-transparent'
            }`} style={{ backgroundColor: formData.stewardAcknowledgment ? 'var(--lk-sage-muted)' : 'var(--fy-surface-muted)' }}>
              <input
                type="checkbox"
                checked={formData.stewardAcknowledgment}
                onChange={(e) => handleInputChange('stewardAcknowledgment', e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded"
                style={{ accentColor: 'var(--lk-sage)' }}
                data-testid="steward-acknowledgment-checkbox"
              />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                  I understand this group&apos;s policy governs what is allowed here.
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
                  Violations may result in content removal, loss of privileges, or group suspension.
                </p>
              </div>
            </label>
            {errors.stewardAcknowledgment && <p className="text-xs text-red-500 mt-2">{errors.stewardAcknowledgment}</p>}
          </Card>

          {/* ═══ E. CREATE GROUP ═══ */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full font-bold"
            style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
            data-testid="create-group-submit"
          >
            {loading ? 'Creating...' : 'Create Group'}
          </Button>
          
          <p className="text-xs text-center" style={{ color: 'var(--fy-text-disabled)' }}>
            By creating this group, a Group Profile will be generated automatically.
            The promotion policy cannot be changed after creation.
          </p>
          
        </form>
      </main>
    </div>
  );
}
